import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { AlertTriangle } from "lucide-react";
import { Blog } from "./BlogTable";
import { blogService } from "../../services/blogService";
import { useToast } from "../ui/ToastContainer";
import { useState } from "react";

interface DeleteBlogDialogProps {
  isOpen: boolean;
  onClose: () => void;
  blog: Blog | null;
  onSuccess: () => void;
}

export function DeleteBlogDialog({
  isOpen,
  onClose,
  blog,
  onSuccess,
}: DeleteBlogDialogProps) {
  const { showSuccess, showError } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!blog) return;

    try {
      setIsDeleting(true);
      await blogService.deleteBlog(blog._id);
      showSuccess("Blog deleted successfully");
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Failed to delete blog:", error);
      showError("Failed to delete blog. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Blog">
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6 p-4 bg-red-50 rounded-lg text-red-700 border border-red-100">
          <div className="shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle size={24} />
          </div>
          <div>
            <p className="font-bold">Are you sure?</p>
            <p className="text-sm opacity-90">
              This action cannot be undone. This will permanently delete the
              blog post.
            </p>
          </div>
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-500 mb-1">Deleting:</p>
          <p className="font-medium text-gray-900 line-clamp-2">
            {blog?.title || "Unknown Blog"}
          </p>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white rounded"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Confirm Delete"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
